import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { theme } from '../../../theme/tokens';
interface MetricChartProps {
  title: string;
  tooltipText?: string;
  currentValue: string;
  growthPercent: number;
  previousValue: string;
  data: any[];
  previousData?: any[];
  dataKey: string;
  xAxisKey?: string;
  yAxisFormatter?: (value: any) => string;
  tooltipValueFormatter?: (value: any) => string;
  tooltipLabel?: string;
  dateFormatter: (date: string) => string;
  updateTime: string;
  chartColor?: string;
}
export function MetricChart({
  title,
  tooltipText,
  currentValue,
  growthPercent,
  previousValue,
  data,
  previousData,
  dataKey,
  xAxisKey = 'date',
  yAxisFormatter,
  tooltipValueFormatter,
  tooltipLabel = '',
  dateFormatter,
  updateTime,
  chartColor = theme.brand.primary
}: MetricChartProps) {
  const {
    t
  } = useTranslation('analytics');
  // CRITICAL: Ensure we have valid data
  if (!data || data.length === 0) {
    console.error(`❌ ${title} - No data available`);
    return null;
  }
  // Get the actual first and last dates from the data
  const firstDate = data[0]?.[xAxisKey];
  const lastDate = data[data.length - 1]?.[xAxisKey];
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1973" data-mp="31kmlan1qobsq">
      <div className="flex items-start justify-between mb-4" data-id="element-1974" data-mp="31nij8bdl70h6">
        <div className="flex-1" data-id="element-1975" data-mp="3fjm77mxvacl7">
          <div className="flex items-center gap-2 mb-2" data-id="element-1976" data-mp="3fikk2k3ktzn4">
            <h3 className="text-sm font-medium text-gray-700" data-id="element-1977" data-mp="3al2l9mj9x0su">{title}</h3>
            {tooltipText && <button className="text-gray-400 hover:text-gray-600 group relative" data-id="element-1978" data-mp="3p9fb01rrpnew">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" data-id="element-1979" data-mp="3kcoujdc1wpsy">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" data-id="element-1980" data-mp="3uad0z0j0rlqy" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" data-id="element-1981" data-mp="585j4pbvdp7x" />
                </svg>
                <div className="hidden group-hover:block absolute left-0 top-6 w-72 p-3 bg-gray-900 text-white text-xs text-left rounded shadow-lg z-10" data-id="element-1982" data-mp="jw9sh2p18oe1">
                  {tooltipText}
                </div>
              </button>}
          </div>
          <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-1983" data-mp="svdby7s435t2">
            {currentValue}
            <span className={`text-sm ml-2 ${growthPercent >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1984" data-mp="xx2o5qr2lpiq">
              {growthPercent >= 0 ? '+' : ''}
              {growthPercent.toFixed(2)}%
            </span>
          </div>
          <div className="text-xs text-gray-500" data-id="element-1985" data-mp="34m8xp4ep0jcy">
            {previousValue} {t('components.MetricChart.previous_period')}
          </div>
        </div>
      </div>
      <div className="h-48 relative" role="img" aria-label={`${title} chart. Current value ${currentValue}, ${growthPercent >= 0 ? 'up' : 'down'} ${Math.abs(growthPercent).toFixed(2)} percent versus previous period (${previousValue}).`} data-id="element-1986" data-mp="1lth5q9vxm08o">
        {/* Custom X-axis labels - Stripe-style alignment */}
        <div className="absolute bottom-0 left-0 right-0 flex pb-1" style={{
        paddingLeft: '40px'
      }} data-id="element-1987" data-mp="24r0i9nhlm0zu">
          <span className="text-xs text-gray-600" data-id="element-1988" data-mp="20porvfqwhbql">
            {dateFormatter(firstDate)}
          </span>
          <span className="text-xs text-gray-600 ml-auto" data-id="element-1989" data-mp="1vrwx8dyvfbip">
            {dateFormatter(lastDate)}
          </span>
        </div>

        <ResponsiveContainer width="100%" height="100%" data-id="element-1990" data-mp="kadq4ai66oev">
          <LineChart data={data} margin={{
          left: 0,
          right: 0,
          top: 5,
          bottom: 20
        }} data-id="element-1991" data-mp="kbyko04hi7cq">
            <CartesianGrid strokeDasharray="0" vertical={false} stroke="#d1d5db" strokeWidth={1} data-id="element-1992" data-mp="236p6t0a68k53" />

            <XAxis dataKey={xAxisKey} tick={false} axisLine={false} tickLine={false} height={0} data-id="element-1993" data-mp="1u8d6u7nf09zz" />

            <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} width={40} tick={{
            fill: '#666',
            fontSize: 12,
            fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
          }} tickCount={5} data-id="element-1994" data-mp="2mwit4yu4a71x" />

            <Tooltip content={({
            active,
            payload
          }) => {
            if (active && payload && payload.length) {
              const currentData = payload.find(p => !p.strokeDasharray);
              const previousData = payload.find(p => p.strokeDasharray);
              return <div className="bg-white p-3 border border-gray-200 rounded shadow-lg" data-id="element-1996" data-mp="tzniojk7osx6">
                      <div className="text-sm font-medium mb-2" data-id="element-1997" data-mp="17z6ce7ca01py">{title}</div>
                      {currentData && <div className="text-sm mb-1" data-id="element-1998" data-mp="12zk328zkj5mf">
                          <span className="text-gray-600" data-id="element-1999" data-mp="12xz5eahyj6kc">
                            {dateFormatter(currentData.payload[xAxisKey])}
                          </span>
                          <div className="font-semibold text-gray-900" data-id="element-2000" data-mp="ba74wujin3yt">
                            {tooltipValueFormatter ? tooltipValueFormatter(currentData.value) : currentData.value}
                            {tooltipLabel && ` ${tooltipLabel}`}
                          </div>
                        </div>}
                      {previousData && <div className="text-sm" data-id="element-2001" data-mp="3noggm0k30u4d">
                          <span className="text-gray-500" data-id="element-2002" data-mp="1rm4pb31fe53i">
                            {dateFormatter(previousData.payload[xAxisKey])}{' '}
                            {t('components.MetricChart.prev')}
                          </span>
                          <div className="font-medium text-gray-600" data-id="element-2003" data-mp="21dr4tbksqqc2">
                            {tooltipValueFormatter ? tooltipValueFormatter(previousData.value) : previousData.value}
                            {tooltipLabel && ` ${tooltipLabel}`}
                          </div>
                        </div>}
                    </div>;
            }
            return null;
          }} data-id="element-1995" data-mp="2hyzdpvlono8e" />

            <Line type="monotone" dataKey={dataKey} stroke={chartColor} strokeWidth={2} dot={false} data-id="element-2004" data-mp="1mlqfbfg9kl2u" />

            {previousData && previousData.length > 0 && <Line type="monotone" dataKey={dataKey} stroke={chartColor} strokeWidth={1.5} strokeDasharray="5 5" dot={false} data={previousData} opacity={0.5} data-id="element-2005" data-mp="mg4uynmm5xq6" />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-xs text-gray-500 mt-2" data-id="element-2006" data-mp="3p5bu31svi4t">{updateTime}</div>
    </div>;
}